import { type fabric } from 'fabric';
import React from 'react';
import ShapeBase from '../shapes/ShapeBase';
import LinearResizeControl from '../controls/LinearResizeControl';
import tools from '../tools';
import '../interactiveOverlayContainer.css';
import EmbeddedExternalContentContainer from './EmbeddedExternalContentContainer';
import { type Constructor } from '../types';
import {
  EMBEDDED_CONTENT_PLACEHOLDER_WIDTH,
  EMBEDDED_CONTENT_PLACEHOLDER_HEIGHT,
  EMBEDDED_CONTENT_PLACEHOLDER_SVG,
  prepareEmbeddedContentPlaceholder,
  encodeSvg,
} from './IconsHelper';

const PLACEHOLDER_RATIO =
  EMBEDDED_CONTENT_PLACEHOLDER_WIDTH / EMBEDDED_CONTENT_PLACEHOLDER_HEIGHT;

export const EMBEDDED_CONTENT_WIDTH = 1080;
export const EMBEDDED_CONTENT_HEIGHT = 720;

export interface EmbeddedContentBehaviorsInterface {
  initializeEmbeddedContentBehaviors(): void;
  hasExternalContent(): boolean;
  clipExternalContent(): boolean;
  renderExternalContent(): JSX.Element;
  get externalContentUrl(): string;
}

interface Embeddable {
  get externalContentUrl(): string;
  onBeginInteraction(): void;
  get interactionMessage(): string;
}

function EmbeddedContentBehaviors(Base: Constructor<fabric.Object>): any {
  return class WithEmbeddedContentBehaviors
    extends (Base as Constructor<fabric.Object & Embeddable>)
    implements EmbeddedContentBehaviorsInterface
  {
    __hasEmbeddedContentBehaviors = true;
    serializedKeys = [
      // @ts-expect-error serializedKeys is declared on subclasses
      ...this.serializedKeys,
      'z',
      'textContent',
      'left',
      'top',
      'width',
      'height',
      'scaleX',
      'scaleY',
      'opacity',
    ];

    uniformScaling = true;
    minScaleLimit = 0.05;

    protected contentLoaded = false;
    protected showInteractLayer = false;

    controls = {
      tl: ShapeBase.resizeControl({ x: -0.5, y: -0.5 }),
      tr: ShapeBase.resizeControl({ x: 0.5, y: -0.5 }),
      bl: ShapeBase.resizeControl({ x: -0.5, y: 0.5 }),
      br: ShapeBase.resizeControl({ x: 0.5, y: 0.5 }),
      mt: new LinearResizeControl({ edge: 'top' }),
      mb: new LinearResizeControl({ edge: 'bottom' }),
      ml: new LinearResizeControl({ edge: 'left' }),
      mr: new LinearResizeControl({ edge: 'right' }),
    };

    initializeEmbeddedContentBehaviors() {
      this.on('mousedblclick', () => this.beginIFrameViewing());

      ['deselected', 'moving', 'rotating', 'scaling'].forEach(event =>
        this.on(event, this.endIFrameViewing)
      );

      this.on('selected', () => this.setShowInteractLayer(true));
      this.on('mousemove', () => this.setShowInteractLayer(true));
      this.on('mouseover', () => this.setShowInteractLayer(true));
      this.on('mouseout', () => this.setShowInteractLayer(false));

      WithEmbeddedContentBehaviors.preparePlaceholder();
    }

    protected static placeholderImage: HTMLImageElement | null = null;

    static defaultWidth() {
      return EMBEDDED_CONTENT_WIDTH;
    }

    static defaultHeight() {
      return EMBEDDED_CONTENT_HEIGHT;
    }

    static preparePlaceholder() {
      if (this.placeholderImage) {
        return;
      }

      this.placeholderImage = prepareEmbeddedContentPlaceholder();
    }

    tools() {
      const superTools = (super.tools && super.tools()) || [];
      return [tools.ToolOpenExternalEmbed, superTools];
    }

    beginIFrameViewing() {
      if (this.canDisplayContent() && this.contentLoaded) {
        this.setShowInteractLayer(false);
        this.canvas?.externalContent?.focus(this);
        this.controller?.actionShowExternalContentExitButton?.(true);
        if (this.onBeginInteraction) {
          this.onBeginInteraction();
        }
      }
    }

    endIFrameViewing = () => {
      this.canvas?.externalContent?.blur();
      this.controller?.actionShowExternalContentExitButton?.(false);
    };

    hasExternalContent() {
      return true;
    }

    clipExternalContent() {
      return !this.canvas?.exporting;
    }

    minDimension({ resizingWidth = true }) {
      return resizingWidth
        ? WithEmbeddedContentBehaviors.defaultWidth()
        : WithEmbeddedContentBehaviors.defaultHeight();
    }

    maxDimension() {
      return Number.MAX_SAFE_INTEGER;
    }

    _render(ctx: CanvasRenderingContext2D) {
      if (this.canvas?.exporting && !this.canvas?.isHTMLExport?.()) {
        this._renderPlaceholder(ctx, this);
      }
    }

    renderExternalContent() {
      const {
        scaleX = 1,
        scaleY = 1,
        width = WithEmbeddedContentBehaviors.defaultWidth(),
        height = WithEmbeddedContentBehaviors.defaultHeight(),
      } = this;

      const canDisplay = this.canDisplayContent();

      if (!canDisplay) {
        this.contentLoaded = false;
        this.showInteractLayer = false;
      }

      const gap = Math.max(4, 12 * scaleX);
      const bottom = Math.min(20, 20 * scaleY);
      const padding = Math.max(4, 20 * scaleX);
      const fontSize = Math.max(4, 14 * scaleX);

      return (
        <div>
          <EmbeddedExternalContentContainer
            url={this.externalContentUrl}
            width={width}
            height={height}
            scale={this.containerScale}
            fontScale={this.fontScale()}
            placeholder={encodeSvg(EMBEDDED_CONTENT_PLACEHOLDER_SVG)}
            showPlaceholder={!canDisplay}
            exporting={Boolean(this.canvas?.exporting)}
            onClick={this.handleIframeClick}
            onLoaded={() => {
              this.contentLoaded = true;
              this.canvas?.externalContent?.render(this);
            }}
          />
          {canDisplay && this.showInteractLayer && (
            <div className='interactive-overlay-container' style={{ bottom }}>
              <div
                className='interactive-overlay-container__content'
                style={{ gap, padding, fontSize }}
              >
                <i className='fas fa-bullseye-pointer'></i>
                {this.interactionMessage}
              </div>
            </div>
          )}
        </div>
      );
    }

    private readonly handleIframeClick = ({
      url,
      newTab,
      useBottomDrawer,
    }: {
      url: string;
      newTab?: boolean;
      useBottomDrawer?: boolean;
    }) => {
      if (url) {
        this.handleLinkClick({ url, newTab, useBottomDrawer });
      } else {
        this.controller?.closeAhaDrawer?.();
      }
    };

    private readonly handleLinkClick = ({
      url,
      newTab,
      useBottomDrawer,
    }: {
      url: string;
      newTab?: boolean;
      useBottomDrawer?: boolean;
    }) => {
      if (url && newTab) {
        window.open(url, '_blank', 'noopener noreferrer');
      } else if (url && this.controller?.allowRecordLinking?.()) {
        this.controller?.props?.openAhaDrawer?.({ url, useBottomDrawer });
      }
    };

    private setShowInteractLayer(show: boolean) {
      const previous = this.showInteractLayer;
      this.showInteractLayer = this.contentLoaded && this.isSelected() && show;

      if (previous !== this.showInteractLayer) {
        this.canvas?.externalContent?.render(this);
      }
    }

    private _renderPlaceholder(
      ctx: CanvasRenderingContext2D,
      obj: fabric.Object
    ) {
      ctx.save();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#cccccc';
      ctx.strokeRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);
      ctx.fillStyle = '#f9f9f9';
      ctx.fillRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);
      ctx.restore();

      const width = obj.getScaledWidth();
      const height = obj.getScaledHeight();
      const imageSize = Math.min(width, height) * 0.4;
      const imageWidth = imageSize * PLACEHOLDER_RATIO;
      const imageHeight = imageSize;
      const placeholderImage = WithEmbeddedContentBehaviors.placeholderImage;

      if (placeholderImage) {
        ctx.drawImage(
          placeholderImage,
          -imageWidth / 2,
          -imageHeight / 2,
          imageWidth,
          imageHeight
        );
      }
    }

    private canDisplayContent() {
      if (this.canvas?.isHTMLExport?.() && this.canvas?.showAhaViews?.()) {
        return true;
      }

      return (
        !window.isPublishing &&
        !window.isKnowledgeBase &&
        !this.controller?.isShared &&
        !this.canvas?.exporting
      );
    }

    private get containerScale() {
      return this.scaleX ?? 1;
    }

    private fontScale() {
      const { width, height } = this;

      return (
        Math.min(
          width / WithEmbeddedContentBehaviors.defaultWidth(),
          height / WithEmbeddedContentBehaviors.defaultHeight()
        ) * this.containerScale
      );
    }
  };
}

export default EmbeddedContentBehaviors;
